import React, { useState, useRef, useEffect } from "react";
import CustomCheckbox from "../../components/CustomCheckbox";
import CustomInput from "../../components/CustomInput";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";
import CustomButton from "../../components/CustomButton";
import { useTranslation } from "react-i18next";
import { sanitizeNumber } from "../../utils/sanitizeNumber";

import addIcon from "../../assets/add_icon.png";

const PaperListedTable = ({
  columns = [],
  withCheckbox = false,
  selectedRows = [],
  setSelectedRows = () => {},
  fullData = [],
  initialData = [],
  handleAddRow,
  onFetchColorName = () => {},
  loadingColorName,
  handleSelectCurrency = {},
  selectedCurrency,
}) => {
  const { t } = useTranslation("general");

  // Create initialRow based on dynamic columns
  const createInitialRow = () => {
    const row = {};
    columns.forEach((column) => {
      row[column.key] = "";
    });
    return row;
  };

  const initialRow = createInitialRow();

  const [rows, setRows] = useState(
    initialData.length > 0 ? initialData : [initialRow]
  );

  const inputRefs = useRef([]);
  const [selectAll, setSelectAll] = useState(false);

  // Initialize inputRefs for each cell
  useEffect(() => {
    inputRefs.current = rows.map(() =>
      Array(columns.length)
        .fill()
        .map(() => React.createRef())
    );
  }, [rows.length, columns.length]);

  useEffect(() => {
    const allSelected =
      rows.length > 0 &&
      rows.every((_, index) => selectedRows.some((item) => item.id === index));
    setSelectAll(allSelected);
  }, [selectedRows, rows]);

  useEffect(() => {
    if (initialData.length === 0) {
      setRows([createInitialRow()]);
    } else {
      setRows(initialData);
    }
  }, [initialData]);

  useEffect(() => {
    handleAddRow(rows);
  }, [rows.length]);

  const handleChange = (e, rowIndex, field) => {
    const newRows = [...rows];
    newRows[rowIndex][field] = e.target.value;

    // Auto-calculate totalAmount if price or quantity changes
    if (field === "quantity" || field === "price") {
      const price = parseFloat(sanitizeNumber(newRows[rowIndex].price) || 0);
      const quantity = parseFloat(
        sanitizeNumber(newRows[rowIndex].quantity) || 0
      );
      newRows[rowIndex].totalAmount = (price * quantity).toFixed(2);
    }

    setRows(newRows);
    handleAddRow(newRows);
  };

  const handleBlur = (e, rowIndex, field) => {
    if (field === "rollCode" && e.target.value.trim() !== "") {
      onFetchColorName(e.target.value.trim(), rowIndex, rows, setRows);
    }
  };

  const handleKeyDown = (e, rowIndex, colIndex) => {
    if (e.key === "Enter") {
      e.preventDefault();

      const isPriceField = e?.target?.name === "price";

      if (isPriceField) {
        setRows([...rows, initialRow]);
        if (
          inputRefs.current[rowIndex + 1] &&
          inputRefs.current[rowIndex + 1][0]
        ) {
          inputRefs.current[rowIndex + 1][0].current?.focus();
        }
      } else {
        const nextCol = colIndex === columns.length - 1 ? 0 : colIndex + 1;
        const nextRow =
          colIndex === columns.length - 1 ? rowIndex + 1 : rowIndex;

        if (inputRefs.current[nextRow] && inputRefs.current[nextRow][nextCol]) {
          inputRefs.current[nextRow][nextCol].current?.focus();
        }
      }
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows([]);
    } else {
      const allRows = rows.map((row, index) => ({
        id: index,
        data: row,
      }));
      setSelectedRows(allRows);
    }
    setSelectAll(!selectAll);
  };

  const handleSelectOne = (id) => {
    const rowData = { id, data: rows.find((_, index) => index === id) };

    setSelectedRows((prevSelected) => {
      const isSelected = prevSelected.some((item) => item.id === id);

      if (isSelected) {
        return prevSelected.filter((item) => item.id !== id);
      } else {
        return [...prevSelected, rowData];
      }
    });
  };

  const handleAddNewRow = () => {
    //     setRows((prevRows) => [...prevRows, createInitialRow()]);

    // Check if there's already an empty row
    const hasEmptyRow = rows.some((row) =>
      Object.values(row).every((value) => value === "")
    );

    // Only add a new row if there isn't an empty one
    if (!hasEmptyRow) {
      setRows((prevRows) => [...prevRows, createInitialRow()]);
    }
  };

  return (
    <>
      <section className="rounded-table-container">
        <table className="i-table">
          <thead className="table-head">
            <tr>
              {withCheckbox && (
                <th className="bold-text">
                  <CustomCheckbox
                    checked={selectAll}
                    onChange={handleSelectAll}
                    color="primary"
                  />
                </th>
              )}
              {columns.map((column, index) => (
                <th
                  key={index}
                  className={`cell-title ${index === 0 ? "bold-text" : ""} ${
                    column.keepOnMobile ? "" : "mobile-hider"
                  }`}
                >
                  {t(column.title)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="table-body">
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {withCheckbox && (
                  <td className="cell the-rounded-first-cell">
                    <CustomCheckbox
                      checked={selectedRows.some(
                        (item) => item.id === rowIndex
                      )}
                      onChange={() => handleSelectOne(rowIndex)}
                    />
                  </td>
                )}
                {columns.map((column, colIndex) => {
                  const isFirstCell = !withCheckbox && colIndex === 0;
                  const isLastCell = colIndex === columns.length - 1;
                  const cellClasses = [
                    "cell",
                    isFirstCell ? "the-rounded-first-cell" : "",
                    isLastCell ? "the-rounded-last-cell" : "",
                    column.keepOnMobile ? "" : "mobile-hider",
                  ]
                    .filter(Boolean)
                    .join(" ");

                  return (
                    <td key={column.key} className={cellClasses}>
                      {column.key === "totalAmount" ? (
                        <div className="u-width-specific_200">
                          <CustomInputWithUnit
                            value={isNaN(row[column.key]) ? 0 : row[column.key]}
                            onChange={(e) =>
                              handleChange(e, rowIndex, column.key)
                            }
                            onBlur={(e) => handleBlur(e, rowIndex, column.key)}
                            onKeyDown={(e) =>
                              handleKeyDown(e, rowIndex, colIndex)
                            }
                            ref={
                              inputRefs.current[rowIndex]
                                ? inputRefs.current[rowIndex][colIndex]
                                : null
                            }
                            unit={selectedCurrency?.value}
                            readonly={true}
                            type="moneyV2"
                          />
                        </div>
                      ) : column.key === "quantity" ? (
                        <div className="o-row o-row--vertical-align_center o-row--gap-size_100">
                          <CustomInputWithUnit
                            value={row[column.key] || ""}
                            onChange={(e) =>
                              handleChange(e, rowIndex, column.key)
                            }
                            onBlur={(e) => handleBlur(e, rowIndex, column.key)}
                            onKeyDown={(e) =>
                              handleKeyDown(e, rowIndex, colIndex)
                            }
                            ref={
                              inputRefs.current[rowIndex]
                                ? inputRefs.current[rowIndex][colIndex]
                                : null
                            }
                            // need to add type number so min att. can work
                            min="0"
                            unit={t("ton")}
                            // type="moneyV2"
                          />
                          <span>{row?.unitOfMeasure?.toLowerCase()}</span>
                        </div>
                      ) : column.key === "dimension" ? (
                        <div className="o-row o-row--vertical-align_center o-row--gap-size_100">
                          <CustomInputWithUnit
                            value={row[column.key] || ""}
                            onChange={(e) =>
                              handleChange(e, rowIndex, column.key)
                            }
                            onBlur={(e) => handleBlur(e, rowIndex, column.key)}
                            onKeyDown={(e) =>
                              handleKeyDown(e, rowIndex, colIndex)
                            }
                            ref={
                              inputRefs.current[rowIndex]
                                ? inputRefs.current[rowIndex][colIndex]
                                : null
                            }
                            min={1}
                            unit={t("cm")}
                            type="moneyV2"
                          />
                          <span>{row?.unitOfMeasure?.toLowerCase()}</span>
                        </div>
                      ) : column.key === "paperThickness" ? (
                        <div className="o-row o-row--vertical-align_center o-row--gap-size_100">
                          <CustomInputWithUnit
                            value={row[column.key] || ""}
                            onChange={(e) =>
                              handleChange(e, rowIndex, column.key)
                            }
                            onBlur={(e) => handleBlur(e, rowIndex, column.key)}
                            onKeyDown={(e) =>
                              handleKeyDown(e, rowIndex, colIndex)
                            }
                            ref={
                              inputRefs.current[rowIndex]
                                ? inputRefs.current[rowIndex][colIndex]
                                : null
                            }
                            min={1}
                            unit={t("gram")}
                            type="moneyV2"
                          />
                          <span>{row?.unitOfMeasure?.toLowerCase()}</span>
                        </div>
                      ) : column.key === "price" ? (
                        <div className="o-row o-row--vertical-align_center o-row--gap-size_100">
                          <CustomInputWithUnit
                            value={row[column.key] || ""}
                            onChange={(e) =>
                              handleChange(e, rowIndex, column.key)
                            }
                            onBlur={(e) => handleBlur(e, rowIndex, column.key)}
                            onKeyDown={(e) =>
                              handleKeyDown(e, rowIndex, colIndex)
                            }
                            ref={
                              inputRefs.current[rowIndex]
                                ? inputRefs.current[rowIndex][colIndex]
                                : null
                            }
                            type="moneyV2"
                            min={1}
                            name="price"
                            unit={selectedCurrency?.value}
                          />
                        </div>
                      ) : column.key === "amountOfRolls" ? (
                        <div className="o-row o-row--vertical-align_center o-row--gap-size_100">
                          <CustomInputWithUnit
                            value={row[column.key] || ""}
                            onChange={(e) =>
                              handleChange(e, rowIndex, column.key)
                            }
                            onBlur={(e) => handleBlur(e, rowIndex, column.key)}
                            onKeyDown={(e) =>
                              handleKeyDown(e, rowIndex, colIndex)
                            }
                            ref={
                              inputRefs.current[rowIndex]
                                ? inputRefs.current[rowIndex][colIndex]
                                : null
                            }
                            min={1}
                            unit={t("rollUnit")}
                            // type="moneyV2"
                          />
                        </div>
                      ) : column.key == "categoryName" ? (
                        <CustomInput
                          value={row[column.key] || ""}
                          onChange={(e) =>
                            handleChange(e, rowIndex, column.key)
                          }
                          onBlur={(e) => handleBlur(e, rowIndex, column.key)}
                          onKeyDown={(e) =>
                            handleKeyDown(e, rowIndex, colIndex)
                          }
                          ref={
                            inputRefs.current[rowIndex]
                              ? inputRefs.current[rowIndex][colIndex]
                              : null
                          }
                          type="text"
                          disabled
                        />
                      ) : (
                        <CustomInput
                          value={row[column.key] || ""}
                          onChange={(e) =>
                            handleChange(e, rowIndex, column.key)
                          }
                          onBlur={(e) => handleBlur(e, rowIndex, column.key)}
                          onKeyDown={(e) =>
                            handleKeyDown(e, rowIndex, colIndex)
                          }
                          ref={
                            inputRefs.current[rowIndex]
                              ? inputRefs.current[rowIndex][colIndex]
                              : null
                          }
                          type="text"
                          loading={loadingColorName}
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <div className="o-row o-row--horizontal-align_end u-margin--top_300">
        <CustomButton
          title={t("addAnotherItem")}
          onClick={handleAddNewRow}
          variant="blank"
          icon={addIcon}
          iconSize={16}
        />
      </div>
    </>
  );
};

export default PaperListedTable;
